import { useEffect, useMemo, useState } from "react";

import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetTaggableSameCompanyUsers } from "@/hooks/queries/useGetTaggableSameCompanyUsers";
import { presence } from "@/lib/presence";
import { storage } from "@/lib/storage";
import { JobPresence } from "@/types";

// Who has been on a job page in the last few minutes, keyed by job id.
//
// The backend only knows where everyone is *right now*, so each poll of the
// all-users query is folded into a capped local history. Rides that query's
// existing 60s refetch — no extra network traffic.
export const useJobPresence = (): Map<string, JobPresence[]> => {
  /* Database CRUD Operations */
  // Read
  const { data: users, dataUpdatedAt } = useGetTaggableSameCompanyUsers();
  const currentUser = useGetCurrentUser();

  /* React Hooks */
  const [visits, setVisits] = useState(() => storage.getJobVisits());

  useEffect(() => {
    if (!users) return;

    setVisits((storedVisits) => {
      const merged = presence.recordVisits(storedVisits, users, {
        excludeUserId: currentUser?.id,
        nowMs: Date.now(),
      });
      storage.setJobVisits(merged);
      return merged;
    });
  }, [users, dataUpdatedAt, currentUser?.id]);

  const usersById = useMemo(
    () => new Map((users ?? []).map((user) => [user.id, user])),
    [users],
  );

  // Recomputed per poll rather than per render, so the window advances with
  // the data instead of thrashing on every parent re-render.
  return useMemo(
    () => presence.byJobId(visits, usersById, Date.now()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visits, usersById, dataUpdatedAt],
  );
};
